import {
  BookOpen,
  Building2,
  ExternalLink,
  FileText,
  Filter,
  PhoneCall,
  Search,
  ShieldCheck,
} from "lucide-react";
import { useMemo, useState } from "react";

import SectionHeader from "./SectionHeader.jsx";


const resourceTopics = [
  {
    title: "Preparing a written consumer complaint",
    category: "Complaint Preparation",
    text: "A strong complaint should explain what happened in date order, identify the business, describe the product or service, and state the specific outcome requested.",
    details: [
      "Include your current mailing address, phone number, and email address.",
      "State both the problem and the action you are requesting.",
      "Use a timeline with important dates, such as purchase, cancellation, repair, delivery, or contact dates.",
      "Attach copies of contracts, work orders, repair orders, bills, receipts, advertisements, screenshots, emails, or other records.",
      "Do not send original documents unless an official agency specifically instructs you to do so.",
    ],
    keywords: ["complaint", "timeline", "records", "documents", "prepare"],
  },
  {
    title: "Trying to resolve the dispute with the business",
    category: "Complaint Preparation",
    text: "When appropriate, contact the business in writing before escalating the issue. A clear written request can create a useful record.",
    details: [
      "Identify the order number, account number, invoice number, or contract number.",
      "Explain the issue briefly and factually.",
      "State the exact resolution requested, such as refund, repair, replacement, cancellation, correction, or written explanation.",
      "Give the business a reasonable response date.",
      "Save every message, confirmation number, letter, and response.",
    ],
    keywords: ["business", "merchant", "seller", "resolve", "letter"],
  },
  {
    title: "30-day demand letters",
    category: "Demand Letters and Court",
    text: "A 30-day demand letter may be relevant when a Massachusetts consumer dispute involves an unfair or deceptive business practice.",
    details: [
      "The letter should describe the complaint, the harm or loss, and the relief requested.",
      "Send copies of supporting records, not originals.",
      "Keep proof of mailing and delivery.",
      "Calendar 30 days from receipt before considering court options based on Chapter 93A.",
      "This portal does not provide legal advice. Consider legal help for court deadlines or legal claims.",
    ],
    keywords: ["demand", "93a", "chapter 93a", "court", "legal"],
  },
  {
    title: "Small claims and court preparation",
    category: "Demand Letters and Court",
    text: "Small claims court may be an option for certain money claims, but it is separate from filing a consumer complaint with an agency.",
    details: [
      "Organize the exact legal name and address of the business or person.",
      "Bring copies of contracts, receipts, letters, photos, estimates, and payment records.",
      "Prepare a short chronological summary.",
      "If using a consumer protection claim, keep a copy of any 30-day demand letter.",
      "For legal advice, contact an attorney, legal aid, or court service resource.",
    ],
    keywords: ["small claims", "court", "damages", "judge", "hearing"],
  },
  {
    title: "Auto repair, sales, financing, and warranty disputes",
    category: "Industry Guidance",
    text: "Auto complaints should identify whether the issue involves repair work, sales, financing, repossession, title, warranty, or insurance.",
    details: [
      "Gather the purchase contract, financing agreement, repair order, estimate, invoice, and warranty.",
      "Record the vehicle year, make, model, VIN, mileage, and dealer or repair shop information.",
      "Explain what was authorized, what was promised, what was completed, and what remains disputed.",
      "Keep photos, inspection reports, diagnostic reports, towing records, and messages.",
    ],
    keywords: ["auto", "car", "vehicle", "dealer", "repair", "lemon", "warranty"],
  },
  {
    title: "Home improvement contractor disputes",
    category: "Industry Guidance",
    text: "Contractor complaints should connect each problem to the contract, payment records, photos, messages, permits, or inspection documents.",
    details: [
      "Gather signed contracts, proposals, change orders, invoices, receipts, and proof of payment.",
      "Take photos or videos showing the work before, during, and after the problem.",
      "Check whether permits, inspections, contractor registration, or licensing information are relevant.",
      "State whether you are requesting completion, repair, refund, itemized accounting, warranty service, or cancellation.",
    ],
    keywords: ["contractor", "home improvement", "roof", "plumbing", "hvac", "deposit"],
  },
  {
    title: "Debt collection and credit reporting",
    category: "Financial and Debt",
    text: "Debt and credit complaints should clearly state whether the debt is recognized, disputed, already paid, the wrong amount, or connected to identity theft.",
    details: [
      "Save collection letters, credit reports, account statements, court papers, and notices.",
      "Keep call logs with dates, times, numbers, and names.",
      "If disputing a debt, communicate carefully in writing and keep copies.",
      "Do not ignore court papers or legal deadlines.",
    ],
    keywords: ["debt", "collection", "credit", "credit report", "loan", "mortgage"],
  },
  {
    title: "Fraud, scams, and identity theft",
    category: "Fraud and Identity Theft",
    text: "Fraud and identity theft issues often require fast action to secure accounts, report the problem, and preserve evidence.",
    details: [
      "Save screenshots of messages, emails, websites, usernames, phone numbers, and payment requests.",
      "Contact banks, card issuers, payment apps, or account providers immediately if money or accounts are at risk.",
      "Consider credit freezes or fraud alerts when appropriate.",
      "Use official reporting channels for identity theft and scams.",
    ],
    keywords: ["fraud", "scam", "identity theft", "phishing", "hacked", "data breach"],
  },
  {
    title: "Housing, landlord, tenant, and deposit issues",
    category: "Housing and Utilities",
    text: "Housing complaints should identify the lease, property address, disputed amount, repair issue, notice, or security deposit concern.",
    details: [
      "Gather leases, rent receipts, notices, repair requests, inspection reports, and photos.",
      "Keep messages with the landlord, property manager, tenant, housing agency, or inspectional services.",
      "If court papers, eviction notices, or urgent housing issues are involved, seek legal or housing help promptly.",
      "State whether you are requesting repair, refund, deposit return, correction, or written explanation.",
    ],
    keywords: ["landlord", "tenant", "rent", "security deposit", "housing", "lease"],
  },
  {
    title: "Utilities, phone, internet, and cable service",
    category: "Housing and Utilities",
    text: "Utility and service provider disputes should identify the account, billing period, disputed charge, service address, and ticket numbers.",
    details: [
      "Gather bills, account statements, contracts, cancellation confirmations, shutoff notices, and service tickets.",
      "Save chat transcripts, technician notes, call logs, and confirmation numbers.",
      "If the issue involves essential service or shutoff, act quickly and document every contact.",
      "Different regulators may handle electric, gas, water, phone, internet, and cable issues.",
    ],
    keywords: ["utility", "electric", "gas", "water", "internet", "cable", "phone"],
  },
];

const resourceDirectory = [
  {
    title: "Cambridge Consumers' Council",
    agency: "City of Cambridge",
    category: "Cambridge and Massachusetts",
    url: "https://www.cambridgema.gov/departments/consumerscouncil",
    description:
      "Official Cambridge Consumers' Council information, complaint process guidance, hotline details, and local consumer resources.",
    bestFor:
      "Cambridge-area consumer assistance, local complaint process information, resources, and referrals.",
    prepare:
      "Prepare your timeline, business information, contact details, and copies of supporting records.",
    phone: "617-349-6150",
    keywords: ["cambridge", "consumer council", "local", "hotline"],
  },
  {
    title: "Massachusetts Attorney General Consumer Complaint",
    agency: "Massachusetts Attorney General",
    category: "Complaint Filing",
    url: "https://www.mass.gov/how-to/file-a-consumer-complaint",
    description:
      "File a consumer complaint with the Attorney General's Office for many business, product, debt, home improvement, utility, and fraud-related issues.",
    bestFor:
      "State consumer complaints when a business problem needs review, assistance, or referral.",
    prepare:
      "Have the business name, address, complaint summary, timeline, requested outcome, and supporting records ready.",
    phone: "617-727-8400",
    keywords: ["attorney general", "consumer complaint", "ago", "card"],
  },
  {
    title: "30 Day Demand Letter",
    agency: "Mass.gov",
    category: "Demand Letters and Court",
    url: "https://www.mass.gov/info-details/30-day-demand-letter",
    description:
      "Official Massachusetts guidance on 30-day demand letters under the consumer protection law.",
    bestFor:
      "Understanding what a 30-day demand letter is, what to include, and why proof of delivery matters.",
    prepare:
      "Gather your name and address, business address, facts, harm or loss, requested relief, and supporting records.",
    keywords: ["demand letter", "30 day", "chapter 93a", "93a"],
  },
  {
    title: "Resolving a Consumer Complaint",
    agency: "Mass.gov",
    category: "Complaint Preparation",
    url: "https://www.mass.gov/info-details/resolving-a-consumer-complaint",
    description:
      "Guidance on writing complaint letters, keeping records, sending copies, and getting third-party assistance.",
    bestFor:
      "Learning how to write to a business and preserve records before filing or escalating.",
    prepare:
      "Prepare copies of receipts, repair orders, warranties, canceled checks, contracts, and correspondence.",
    keywords: ["resolve", "complaint letter", "seller", "merchant"],
  },
  {
    title: "Massachusetts Consumer Protection Law",
    agency: "Mass.gov",
    category: "Demand Letters and Court",
    url: "https://www.mass.gov/info-details/the-massachusetts-consumer-protection-law",
    description:
      "Overview of Massachusetts consumer protection law, including unfair or deceptive business practices.",
    bestFor:
      "General background on consumer protection claims and Chapter 93A concepts.",
    prepare:
      "Review the facts, harm, loss, business conduct, and whether a demand letter may be relevant.",
    keywords: ["consumer protection law", "chapter 93a", "unfair", "deceptive"],
  },
  {
    title: "Small Claims Court",
    agency: "Mass.gov",
    category: "Demand Letters and Court",
    url: "https://www.mass.gov/info-details/small-claims-court",
    description:
      "Massachusetts small claims court information, including claim limits, filing basics, evidence preparation, and hearing guidance.",
    bestFor:
      "Consumers considering court after informal resolution, mediation, or complaint options have not resolved the dispute.",
    prepare:
      "Prepare the correct legal name and address of the business, claim amount, timeline, evidence, and demand letter if applicable.",
    keywords: ["small claims", "court", "hearing", "damages"],
  },
  {
    title: "Consumer Protection with the Courts",
    agency: "Massachusetts Court System",
    category: "Demand Letters and Court",
    url: "https://www.mass.gov/consumer-protection-with-the-courts",
    description:
      "Court-system consumer protection resources, including demand letters, small claims, and suing over a consumer complaint.",
    bestFor:
      "Understanding court-related options and self-help resources for consumer protection matters.",
    prepare:
      "Review court forms, demand letter requirements, claim amount, and evidence before taking court action.",
    keywords: ["court", "consumer protection", "lawsuit", "self help"],
  },
  {
    title: "Division of Banks Complaint Form",
    agency: "Massachusetts Division of Banks",
    category: "Financial and Debt",
    url: "https://www.mass.gov/forms/division-of-banks-complaint-form",
    description:
      "Complaint form for issues involving Massachusetts-regulated banks, credit unions, money transmitters, debt collectors, mortgage lenders, and related licensees.",
    bestFor:
      "Banking, mortgage, debt collection, loan servicing, check cashing, and money transmission complaints involving DOB-regulated entities.",
    prepare:
      "Prepare statements, account numbers, letters, transaction records, dispute history, and the business license or institution name.",
    phone: "617-956-1501",
    keywords: ["bank", "mortgage", "debt collector", "loan", "money transfer"],
  },
  {
    title: "Consumer Financial Protection Bureau Complaint",
    agency: "CFPB",
    category: "Financial and Debt",
    url: "https://www.consumerfinance.gov/complaint/",
    description:
      "Federal complaint portal for credit reports, debt collection, mortgages, bank accounts, credit cards, money transfers, loans, and other financial products.",
    bestFor:
      "Financial complaints involving banks, lenders, credit bureaus, debt collectors, payment products, or mortgage servicers.",
    prepare:
      "Prepare account details, transaction dates, complaint summary, company response, and supporting documents.",
    keywords: ["cfpb", "credit report", "debt", "mortgage", "bank", "loan"],
  },
  {
    title: "ReportFraud.ftc.gov",
    agency: "Federal Trade Commission",
    category: "Fraud and Identity Theft",
    url: "https://reportfraud.ftc.gov/",
    description:
      "Federal reporting portal for scams, fraud, suspicious business practices, and marketplace misconduct.",
    bestFor:
      "Reporting scams, imposter schemes, online fraud, fake prizes, deceptive offers, and unfair business practices.",
    prepare:
      "Save screenshots, phone numbers, emails, websites, payment details, dates, and the amount lost.",
    keywords: ["ftc", "fraud", "scam", "report fraud", "imposter"],
  },
  {
    title: "IdentityTheft.gov",
    agency: "Federal Trade Commission",
    category: "Fraud and Identity Theft",
    url: "https://www.identitytheft.gov/",
    description:
      "Federal identity theft reporting and recovery resource with personalized recovery steps.",
    bestFor:
      "Identity theft, fraudulent accounts, stolen personal information, and account recovery planning.",
    prepare:
      "Gather fraudulent account notices, credit reports, collection letters, transaction records, and affected account details.",
    keywords: ["identity theft", "fraudulent account", "credit freeze", "recovery"],
  },
  {
    title: "AnnualCreditReport.com",
    agency: "Authorized Credit Report Source",
    category: "Financial and Debt",
    url: "https://www.annualcreditreport.com/",
    description:
      "Official source for free credit reports from the three nationwide credit reporting companies.",
    bestFor:
      "Checking credit reports for errors, identity theft, collections, unknown accounts, or inaccurate information.",
    prepare:
      "Have identifying information ready and review reports carefully for incorrect addresses, accounts, inquiries, or balances.",
    keywords: ["credit report", "experian", "equifax", "transunion"],
  },
  {
    title: "Department of Public Utilities Consumer Division",
    agency: "Massachusetts DPU",
    category: "Housing and Utilities",
    url: "https://www.mass.gov/orgs/consumer-division",
    description:
      "Assistance with problems involving electric, gas, and investor-owned water utility companies.",
    bestFor:
      "Utility billing, shutoff, service, payment, and electric, gas, or water company disputes.",
    prepare:
      "Prepare bills, account number, service address, shutoff notices, payment records, and prior contact with the utility.",
    phone: "877-886-5066",
    keywords: ["utility", "electric", "gas", "water", "shutoff", "bill"],
  },
  {
    title: "Department of Telecommunications and Cable",
    agency: "Massachusetts DTC",
    category: "Housing and Utilities",
    url: "https://www.mass.gov/orgs/department-of-telecommunications-and-cable",
    description:
      "Massachusetts agency for telecommunications and cable consumer issues.",
    bestFor:
      "Cable, telecommunications, phone service, billing, service quality, and related complaint questions.",
    prepare:
      "Prepare account number, bills, service tickets, cancellation requests, chat transcripts, and call logs.",
    phone: "800-392-6066",
    keywords: ["cable", "phone", "telecommunications", "internet", "service"],
  },
  {
    title: "FCC Consumer Complaints",
    agency: "Federal Communications Commission",
    category: "Federal",
    url: "https://consumercomplaints.fcc.gov/hc/en-us",
    description:
      "Federal complaint center for phone, internet, TV, radio, accessibility, and communications issues.",
    bestFor:
      "Robocalls, unwanted calls, phone service, internet service, TV, radio interference, and communications complaints.",
    prepare:
      "Prepare account details, provider name, dates, phone numbers, screenshots, recordings, or service records when available.",
    keywords: ["fcc", "phone", "internet", "robocall", "communications"],
  },
  {
    title: "Home Improvement Contractor Complaint",
    agency: "Massachusetts OCABR",
    category: "Industry Specific",
    url: "https://www.mass.gov/info-details/file-a-complaint-against-a-home-improvement-contractor",
    description:
      "Official process for filing complaints against home improvement contractors in Massachusetts.",
    bestFor:
      "Home improvement contractor disputes involving contracts, payments, photos, permits, incomplete work, or poor workmanship.",
    prepare:
      "Prepare a summary, copies of payments, up to five photos, the signed contract, and building application if available.",
    phone: "888-283-3757",
    keywords: ["home improvement", "contractor", "hic", "unfinished work", "deposit"],
  },
  {
    title: "HIC Homeowner Resources",
    agency: "Massachusetts OCABR",
    category: "Industry Specific",
    url: "https://www.mass.gov/info-details/hic-homeowner-resources",
    description:
      "Homeowner guidance on hiring contractors, checking registration, contracts, complaints, arbitration, and the guaranty fund.",
    bestFor:
      "Learning how home improvement contractor registration, arbitration, complaints, and homeowner protections work.",
    prepare:
      "Check contractor registration, gather contract documents, payment records, photos, permit information, and written communications.",
    keywords: ["homeowner", "contractor registration", "arbitration", "guaranty fund"],
  },
  {
    title: "Federal Trade Commission Consumer Advice",
    agency: "FTC",
    category: "Federal",
    url: "https://consumer.ftc.gov/",
    description:
      "Consumer education on scams, online safety, money, credit, shopping, privacy, and reporting problems.",
    bestFor:
      "Learning about scams, shopping rights, identity theft, credit, privacy, and common consumer issues.",
    prepare:
      "Search by issue type and save relevant guidance for your records.",
    keywords: ["ftc", "consumer advice", "scams", "shopping", "privacy"],
  },
  {
    title: "National Do Not Call Registry",
    agency: "Federal Trade Commission",
    category: "Federal",
    url: "https://www.donotcall.gov/",
    description:
      "Federal registry for reducing telemarketing calls to personal phone numbers.",
    bestFor:
      "Unwanted telemarketing calls and robocall prevention steps.",
    prepare:
      "Have the phone number you want to register or check.",
    keywords: ["do not call", "telemarketing", "robocalls"],
  },
  {
    title: "Office of Consumer Affairs and Business Regulation",
    agency: "Massachusetts OCABR",
    category: "Cambridge and Massachusetts",
    url: "https://www.mass.gov/orgs/office-of-consumer-affairs-and-business-regulation",
    description:
      "Massachusetts consumer affairs office overseeing consumer resources, business regulation, and several consumer-facing programs.",
    bestFor:
      "General Massachusetts consumer information, referrals, contractor resources, consumer hotline, and regulated industry guidance.",
    prepare:
      "Identify the industry, business, complaint type, and records before contacting the office or hotline.",
    phone: "888-283-3757",
    keywords: ["ocabR", "consumer affairs", "business regulation", "hotline"],
  },
  {
    title: "MassLegalHelp",
    agency: "Massachusetts Legal Help",
    category: "Legal Help",
    url: "https://www.masslegalhelp.org/",
    description:
      "Legal information and self-help resources for Massachusetts residents on housing, benefits, family, debt, and other legal topics.",
    bestFor:
      "Consumers who need legal information, especially for housing, debt, benefits, domestic issues, or court-related questions.",
    prepare:
      "Search by topic and consider legal aid if the issue involves court papers, deadlines, eviction, debt lawsuits, or urgent legal risk.",
    keywords: ["legal help", "housing", "debt", "court", "legal aid"],
  },
];

const categories = [
  "All",
  "Complaint Preparation",
  "Cambridge and Massachusetts",
  "Complaint Filing",
  "Demand Letters and Court",
  "Financial and Debt",
  "Fraud and Identity Theft",
  "Housing and Utilities",
  "Industry Specific",
  "Federal",
  "Legal Help",
];

function matchesQuery(item, query) {
  const value = query.trim().toLowerCase();

  if (!value) {
    return true;
  }

  return [
    item.title,
    item.category,
    item.text,
    item.description,
    item.bestFor,
    item.prepare,
    item.agency,
    ...(item.details || []),
    ...(item.keywords || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(value);
}

export default function Resources() {
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredTopics = useMemo(() => {
    return resourceTopics.filter((topic) => {
      const categoryMatches =
        selectedCategory === "All" || topic.category === selectedCategory;

      return categoryMatches && matchesQuery(topic, query);
    });
  }, [query, selectedCategory]);

  const filteredResources = useMemo(() => {
    return resourceDirectory.filter((resource) => {
      const categoryMatches =
        selectedCategory === "All" || resource.category === selectedCategory;

      return categoryMatches && matchesQuery(resource, query);
    });
  }, [query, selectedCategory]);

  return (
    <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <SectionHeader
        eyebrow="Consumer Resources"
        title="Detailed information, referrals, and complaint resources"
      >
        Search by issue, agency, document type, or next step to find practical
        consumer guidance, official complaint links, and referral resources.
      </SectionHeader>

      <section className="mt-8 rounded-md border border-civic-line bg-white p-5 shadow-sm">
        <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
          <div>
            <label className="label" htmlFor="resource-search">
              Search resources
            </label>

            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-slate-400"
                aria-hidden="true"
              />

              <input
                id="resource-search"
                type="search"
                className="field pl-10"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search demand letter, contractor, credit report, utility, fraud, housing..."
              />
            </div>

            <p className="help-text">
              Search across preparation topics, official complaint links,
              agencies, industry-specific resources, and suggested records.
            </p>
          </div>

          <div className="rounded-md bg-civic-sky px-4 py-3 text-sm font-bold text-civic-blue">
            {filteredTopics.length + filteredResources.length} matches
          </div>
        </div>

        <div className="mt-5">
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-civic-navy">
            <Filter className="h-4 w-4 text-civic-blue" aria-hidden="true" />
            Filter by resource type
          </div>

          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => setSelectedCategory(category)}
                className={`focus-ring rounded-md px-3 py-2 text-sm font-bold transition ${
                  selectedCategory === category
                    ? "bg-civic-blue text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-civic-sky hover:text-civic-blue"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="mt-8 grid gap-6 lg:grid-cols-[.95fr_1.05fr]">
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-civic-blue" aria-hidden="true" />
            <h2 className="text-xl font-black text-civic-navy">
              Searchable guidance topics
            </h2>
          </div>

          {filteredTopics.length > 0 ? (
            filteredTopics.map((topic) => (
              <article
                key={topic.title}
                className="rounded-md border border-civic-line bg-white p-5 shadow-sm"
              >
                <div className="text-xs font-bold uppercase text-civic-blue">
                  {topic.category}
                </div>

                <h3 className="mt-2 text-lg font-bold text-civic-navy">
                  {topic.title}
                </h3>

                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {topic.text}
                </p>

                <ul className="mt-4 space-y-2 text-sm leading-6 text-slate-700">
                  {topic.details.map((detail) => (
                    <li key={detail} className="flex gap-2">
                      <ShieldCheck
                        className="mt-1 h-4 w-4 shrink-0 text-civic-blue"
                        aria-hidden="true"
                      />
                      {detail}
                    </li>
                  ))}
                </ul>
              </article>
            ))
          ) : (
            <div className="rounded-md border border-civic-line bg-white p-5 text-sm text-slate-600">
              No guidance topics matched your search.
            </div>
          )}
        </section>

        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-civic-blue" aria-hidden="true" />
            <h2 className="text-xl font-black text-civic-navy">
              Official links and referral resources
            </h2>
          </div>

          {filteredResources.length > 0 ? (
            filteredResources.map((resource) => (
              <article
                key={resource.url}
                className="rounded-md border border-civic-line bg-white p-5 shadow-sm transition hover:border-civic-blue hover:shadow-panel"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs font-bold uppercase text-civic-blue">
                      {resource.category}
                    </div>

                    <h3 className="mt-2 text-lg font-bold text-civic-navy">
                      {resource.title}
                    </h3>

                    <p className="mt-1 text-sm font-semibold text-slate-500">
                      {resource.agency}
                    </p>
                  </div>

                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="focus-ring rounded-md p-2 text-civic-blue hover:bg-civic-sky"
                    aria-label={`Open ${resource.title}`}
                  >
                    <ExternalLink className="h-5 w-5" aria-hidden="true" />
                  </a>
                </div>

                <p className="mt-3 text-sm leading-6 text-slate-600">
                  {resource.description}
                </p>

                <div className="mt-4 grid gap-3">
                  <div className="rounded-md bg-slate-50 p-3">
                    <div className="flex items-center gap-2 text-sm font-bold text-civic-navy">
                      <FileText
                        className="h-4 w-4 text-civic-blue"
                        aria-hidden="true"
                      />
                      Best for
                    </div>
                    <p className="mt-1 text-sm leading-6 text-slate-600">
                      {resource.bestFor}
                    </p>
                  </div>

                  <div className="rounded-md bg-slate-50 p-3">
                    <div className="flex items-center gap-2 text-sm font-bold text-civic-navy">
                      <ShieldCheck
                        className="h-4 w-4 text-civic-blue"
                        aria-hidden="true"
                      />
                      Prepare before using this resource
                    </div>
                    <p className="mt-1 text-sm leading-6 text-slate-600">
                      {resource.prepare}
                    </p>
                  </div>
                </div>

                {resource.phone && (
                  <div className="mt-4 inline-flex items-center gap-2 rounded-md bg-civic-sky px-3 py-2 text-sm font-bold text-civic-blue">
                    <PhoneCall className="h-4 w-4" aria-hidden="true" />
                    {resource.phone}
                  </div>
                )}
              </article>
            ))
          ) : (
            <div className="rounded-md border border-civic-line bg-white p-5 text-sm text-slate-600">
              No referral links matched your search.
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
